// Rule Setting API service
// Integrates with wht-mt-data-management backend APIs for section rules CRUD, search, and download.

import {
    MT_RULE_SETTING_CONDITIONS_API,
    MT_RULE_SETTING_CREATE_API,
    MT_RULE_SETTING_DELETE_API,
    MT_RULE_SETTING_DOWNLOAD_API,
    MT_RULE_SETTING_SEARCH_API,
    MT_RULE_SETTING_SERVICE_VALUES_API,
    MT_RULE_SETTING_UPDATE_API,
    MT_TRANSACTION_DATA_META_DATA,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
    mtBuildRequestHeader,
    mtHandleErrorResponse,
    mtHandleRequestFailure,
    mtUrlBuilder,
    type MTApiResponse,
} from '@/multi-territory/mt-services/mt-api-utils'

// --- Request/Response types ---

export interface RuleCondition {
  conditionId?: string
  conditionName: string
  operatorName: string
  conditionValue: string
  logicalOperator: string
}

export interface RuleSearchPageFilter {
  currentPage: number
  pageSize: number
  sortField: string
  sortOrder: string
}

export interface RuleSearchFilter {
  fieldName: string
  operator: string
  value?: string
  filterOperator?: string
}

export interface RuleSearchRequest {
  entityMasterUuid: string | null
  tanMasterUuid: string | null
  searchFilters: RuleSearchFilter[]
  pageFilter: RuleSearchPageFilter
}

export interface RuleCreateRequest {
  ruleName: string
  entityMasterUuid: string | null
  tanMasterUuid: string | null
  mappedServiceValue: string
  startDate: string
  endDate: string
  active: boolean
  conditions: RuleCondition[]
}

export interface RuleUpdateRequest {
  ruleId: string
  entityMasterUuid: string | null
  tanMasterUuid: string | null
  startDate: string
  endDate: string
  active: boolean
  conditions: RuleCondition[]
}

export interface RuleCreateUpdateResponse {
  uuid: string
  message: string
}

export interface RuleDeleteRequest {
  entityMasterUuid: string | null
  tanMasterUuid: string | null
}

export interface RuleDownloadRequest {
  entityMasterUuid: string | null
  tanMasterUuid: string | null
  searchFilters: RuleSearchFilter[]
}

/**
 * Options for Rule Setting API calls.
 * tanApplicable determines which identifier field is populated:
 * - true  -> tanMasterUuid = identifierUuid, entityMasterUuid = null
 * - false -> entityMasterUuid = identifierUuid, tanMasterUuid = null
 */
export interface RuleSettingApiOptions {
  tanApplicable: boolean
}

/**
 * Builds the entityMasterUuid / tanMasterUuid fields for request bodies.
 * @param identifierUuid - The selected TAN uuid (when tanApplicable) or entity uuid (when not)
 * @param tanApplicable - Whether TAN is applicable for the selected entity
 */
function buildIdentifierFields(
  identifierUuid: string,
  tanApplicable: boolean
): { entityMasterUuid: string | null; tanMasterUuid: string | null } {
  return tanApplicable
    ? { tanMasterUuid: identifierUuid, entityMasterUuid: null }
    : { entityMasterUuid: identifierUuid, tanMasterUuid: null }
}

// --- Helper: Build Rule Setting headers ---

/**
 * Builds extra headers required by rule setting APIs.
 * X-Country-Code is required by the backend for country-scoped operations.
 */
function buildRuleSettingHeaders(countryCode: string): Record<string, string> {
  return {
    'X-Country-Code': countryCode,
  }
}

// --- Custom error parser for Rule Setting APIs ---

/**
 * Parses JSON response handling the Rule Setting error format.
 * Rule Setting APIs return errors as a string array: { success: false, errors: ["msg1", "msg2"] }
 * This differs from other MT APIs which return { errors: [{ message: "..." }] }.
 */
async function parseRuleSettingJsonResponse<T = unknown>(
  response: Response
): Promise<MTApiResponse<T>> {
  const json = await response.json()

  // Check for validation errors: { success: false, errors: ["..."] }
  if (json && json.success === false && json.errors) {
    const errors = json.errors
    const errorMsg = Array.isArray(errors)
      ? errors
          .map((e: unknown) =>
            typeof e === 'string' ? e : ((e as any)?.message ?? String(e))
          )
          .join(', ')
      : String(errors)
    return { success: false, error: errorMsg }
  }

  // Unwrap nested data: { data: <payload>, success: true }
  const innerData = json?.data

  // Check for validation errors nested in data
  if (
    innerData &&
    typeof innerData === 'object' &&
    innerData.success === false &&
    innerData.errors
  ) {
    const errors = innerData.errors
    const errorMsg = Array.isArray(errors)
      ? errors
          .map((e: unknown) =>
            typeof e === 'string' ? e : ((e as any)?.message ?? String(e))
          )
          .join(', ')
      : String(errors)
    return { success: false, error: errorMsg }
  }

  // Return unwrapped data (or json itself if no data key)
  return { success: true, data: (innerData ?? json) as T }
}

/**
 * Parses message response handling the Rule Setting string-array error format.
 */
async function parseRuleSettingMessageResponse(
  response: Response
): Promise<MTApiResponse<string>> {
  const json = await response.json()

  // Check for validation errors: { success: false, errors: ["..."] }
  if (json && json.success === false && json.errors) {
    const errors = json.errors
    const errorMsg = Array.isArray(errors)
      ? errors
          .map((e: unknown) =>
            typeof e === 'string' ? e : ((e as any)?.message ?? String(e))
          )
          .join(', ')
      : String(errors)
    return { success: false, error: errorMsg }
  }

  const innerData = json?.data

  // Check for validation errors nested in data
  if (
    innerData &&
    typeof innerData === 'object' &&
    innerData.success === false &&
    innerData.errors
  ) {
    const errors = innerData.errors
    const errorMsg = Array.isArray(errors)
      ? errors
          .map((e: unknown) =>
            typeof e === 'string' ? e : ((e as any)?.message ?? String(e))
          )
          .join(', ')
      : String(errors)
    return { success: false, error: errorMsg }
  }

  // Extract message string
  const message: string =
    typeof innerData === 'string' ? innerData : (innerData ?? '')
  return { success: true, data: message }
}

// --- API Functions ---

/**
 * Fetches available nature-of-service values for a given country.
 * Used to populate the mapped service value dropdown in rule creation.
 * GET /wht-mt-data-management/api/v1/section-rules/service-master-values/{countryCode}
 *
 * Headers: Authorization only
 */
export async function fetchServiceMasterValues(
  countryCode: string
): Promise<MTApiResponse<string[]>> {
  try {
    const endpoint = MT_RULE_SETTING_SERVICE_VALUES_API(countryCode)
    const url = mtUrlBuilder(endpoint)
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await parseRuleSettingJsonResponse<string[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Searches rules with pagination and optional filters.
 * POST /wht-mt-data-management/api/v1/section-rules/search
 *
 * Headers: Authorization, X-Country-Code
 * Body: { entityMasterUuid | tanMasterUuid, searchFilters, pageFilter }
 *
 * @param identifierUuid - The selected TAN uuid (when tanApplicable) or entity uuid (when not)
 * @param options.tanApplicable - determines which identifier field to populate
 */
export async function searchRules(
  countryCode: string,
  identifierUuid: string,
  payload: Omit<RuleSearchRequest, 'entityMasterUuid' | 'tanMasterUuid'>,
  options: RuleSettingApiOptions
): Promise<MTApiResponse<unknown>> {
  try {
    const url = mtUrlBuilder(MT_RULE_SETTING_SEARCH_API)
    const extraHeaders = buildRuleSettingHeaders(countryCode)
    const identifierFields = buildIdentifierFields(
      identifierUuid,
      options.tanApplicable
    )
    const requestBody: RuleSearchRequest = {
      ...identifierFields,
      searchFilters: payload.searchFilters,
      pageFilter: payload.pageFilter,
    }
    const requestInit = mtBuildRequestHeader('POST', requestBody, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await parseRuleSettingJsonResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Creates a new section rule with conditions.
 * POST /wht-mt-data-management/api/v1/section-rules/create
 *
 * Headers: Authorization, X-Country-Code
 * Body: { entityMasterUuid | tanMasterUuid, ruleName, mappedServiceValue, startDate, endDate, active, conditions }
 */
export async function createRule(
  countryCode: string,
  payload: RuleCreateRequest
): Promise<MTApiResponse<RuleCreateUpdateResponse>> {
  try {
    const url = mtUrlBuilder(MT_RULE_SETTING_CREATE_API)
    const extraHeaders = buildRuleSettingHeaders(countryCode)
    const requestInit = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await parseRuleSettingJsonResponse<RuleCreateUpdateResponse>(
      response
    )
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Updates an existing section rule and its conditions.
 * POST /wht-mt-data-management/api/v1/section-rules/update
 *
 * Headers: Authorization, X-Country-Code
 * Body: { entityMasterUuid | tanMasterUuid, ruleId, startDate, endDate, active, conditions }
 */
export async function updateRule(
  countryCode: string,
  payload: RuleUpdateRequest
): Promise<MTApiResponse<RuleCreateUpdateResponse>> {
  try {
    const url = mtUrlBuilder(MT_RULE_SETTING_UPDATE_API)
    const extraHeaders = buildRuleSettingHeaders(countryCode)
    const requestInit = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await parseRuleSettingJsonResponse<RuleCreateUpdateResponse>(
      response
    )
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Deletes a section rule by UUID.
 * DELETE /wht-mt-data-management/api/v1/section-rules/{ruleUuid}
 *
 * Headers: Authorization, X-Country-Code
 * Body: { entityMasterUuid | tanMasterUuid }
 *
 * @param identifierUuid - The selected TAN uuid (when tanApplicable) or entity uuid (when not)
 * @param options.tanApplicable - determines which identifier field to populate
 */
export async function deleteRule(
  countryCode: string,
  ruleUuid: string,
  identifierUuid: string,
  options: RuleSettingApiOptions
): Promise<MTApiResponse<string>> {
  try {
    const endpoint = MT_RULE_SETTING_DELETE_API(ruleUuid)
    const url = mtUrlBuilder(endpoint)
    const extraHeaders = buildRuleSettingHeaders(countryCode)
    const identifierFields = buildIdentifierFields(
      identifierUuid,
      options.tanApplicable
    )
    const requestInit = mtBuildRequestHeader(
      'DELETE',
      identifierFields,
      extraHeaders
    )
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await parseRuleSettingMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetches conditions for a specific rule by UUID.
 * GET /wht-mt-data-management/api/v1/section-rules/conditions/{ruleUuid}
 *
 * Headers: Authorization only
 */
export async function fetchRuleConditions(
  ruleUuid: string
): Promise<MTApiResponse<RuleCondition[]>> {
  try {
    const endpoint = MT_RULE_SETTING_CONDITIONS_API(ruleUuid)
    const url = mtUrlBuilder(endpoint)
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await parseRuleSettingJsonResponse<RuleCondition[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads rules as an Excel file (Blob).
 * POST /wht-mt-data-management/api/v1/section-rules/download
 *
 * Headers: Authorization, X-Country-Code
 * Body: { entityMasterUuid | tanMasterUuid, searchFilters }
 *
 * @param identifierUuid - The selected TAN uuid (when tanApplicable) or entity uuid (when not)
 * @param options.tanApplicable - determines which identifier field to populate
 */
export async function downloadRules(
  countryCode: string,
  identifierUuid: string,
  searchFilters: RuleSearchFilter[] = [],
  options: RuleSettingApiOptions
): Promise<MTApiResponse<Blob>> {
  try {
    const url = mtUrlBuilder(MT_RULE_SETTING_DOWNLOAD_API)
    const extraHeaders = buildRuleSettingHeaders(countryCode)
    const identifierFields = buildIdentifierFields(
      identifierUuid,
      options.tanApplicable
    )
    const payload: RuleDownloadRequest = {
      ...identifierFields,
      searchFilters,
    }
    const requestInit = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Utility Functions ---

/**
 * Triggers a file download from a Blob response.
 * Creates a temporary anchor element to initiate browser download.
 */
export function triggerRuleDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(url)
}

// --- Transactional Data Metadata Types ---

export interface TransactionalFieldValidation {
  required?: boolean
  required_message?: string
  regex?: string
  maxLength?: number
  min?: number
  lookupTable?: string
  dateFormat?: string
  conditionallyRequired?: boolean
  requiredWhen?: string
}

export interface TransactionalFieldOption {
  label: string
  value: string
}

export interface TransactionalField {
  field_key: string
  label: string
  type: string
  required: boolean
  placeholder: string
  order: number
  editable: boolean
  visible: boolean
  /** Tool-generated/computed columns (suggestions, variance, status, reference
   * numbers, exclude flag, etc.) — not valid as user-defined rule parameters. */
  system_generated?: boolean
  validations: TransactionalFieldValidation
  /** Present for dropdown-type fields. */
  options?: TransactionalFieldOption[]
  date_format?: string
}

/**
 * Fetches transactional data metadata (field definitions).
 * GET /wht-mt-data-management/api/v1/data/{countryCode}/TRANSACTIONAL_DATA/metadata
 *
 * Returns field list with labels, types, and validations for condition field options.
 */
export async function fetchTransactionalDataMetadata(
  countryCode: string
): Promise<MTApiResponse<TransactionalField[]>> {
  try {
    const url = mtUrlBuilder(MT_TRANSACTION_DATA_META_DATA(countryCode))
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const json = await response.json()
    const fields = json?.data?.fields ?? []
    return { success: true, data: fields }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
